import { createListenerMiddleware } from '@reduxjs/toolkit';
import { clearPageImageCaches } from 'src/utils/pageImageSession';
import { resetUploadQueue } from 'src/utils/uploadQueueStore';
import { logoutAction, syncAuthFromStorageAction } from '../slices/authSlice';
import { getApiOrigin } from './baseQuery';

export const LOGOUT_PATH = '/api/v1/auth/logout';

/**
 * Tell the API we log out. Its response carries `Clear-Site-Data: "cache"`,
 * so the browser drops its HTTP cache for the site. Best effort: logout
 * never waits for it or fails on it.
 */
export const notifyServerLogout = (baseUrl = getApiOrigin()) => {
  if (typeof fetch !== 'function') return;
  try {
    void fetch(baseUrl + LOGOUT_PATH, {
      method: 'POST',
      cache: 'no-store',
      credentials: 'omit',
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* no network: the session is gone locally anyway */
  }
};

/**
 * On logout: drop in-memory page images and the upload queue (running uploads
 * stop), and ask the API to clear caches.
 */
export const logoutListener = createListenerMiddleware();

logoutListener.startListening({
  actionCreator: logoutAction,
  effect: () => {
    clearPageImageCaches();
    resetUploadQueue();
    notifyServerLogout();
  },
});

// Logged out in another tab (token removed): drop the images and the upload
// queue here too. That tab already called the API.
logoutListener.startListening({
  actionCreator: syncAuthFromStorageAction,
  effect: (_action, api) => {
    const state = api.getState() as { auth?: { token?: string } };
    if (state.auth?.token) return;
    clearPageImageCaches();
    resetUploadQueue();
  },
});
